import { motion } from "framer-motion";
import { Brain, Gift, CheckCircle2 } from "lucide-react";

const INTEL = [
  { k: "Audience", v: "Francophone professionals preparing for DELF B2 certification" },
  { k: "Pain Points", v: "Speaking anxiety, exam format confusion, writing structure" },
  { k: "Content Themes", v: "Exam strategy, mock tests, study routines, success stories" },
  { k: "Recurring Questions", v: "\u201CHow long to prepare for B2?\u201D \u201CWhich books actually work?\u201D" },
  { k: "Value Opportunity", v: "Free live masterclass on the B2 oral exam" },
  { k: "Recommended Partnership", v: "Educational presentation + community resource" },
  { k: "Do NOT Pitch", v: "Direct product promotion — group rules prohibit it" },
  { k: "Confidence", v: "94% — based on 60 days of observed activity" },
];

const VALUE_OFFERS = [
  "Free Guide",
  "Masterclass",
  "Webinar",
  "Expert Interview",
  "Q&A Session",
  "Diagnostic Test",
  "Checklist",
  "Mini-Training",
  "Research & Insights",
  "Community Challenge",
];

export default function Intelligence() {
  return (
    <section data-testid="intelligence-section" className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            Research &amp; Value-First Engines
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Know the room before you walk into it.
          </h2>
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            data-testid="partnership-intelligence-card"
            className="border border-[#27272A] bg-[#0A0A0A] p-8 transition-colors duration-300 hover:border-[#D4AF37]/40 lg:p-10"
          >
            <div className="flex items-center gap-3">
              <Brain className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-300">
                Partnership Intelligence
              </h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-500">
              For every high-priority prospect, the Research Agent builds a full intelligence
              profile from public signals — who they are, what their audience asks, and exactly
              where the opportunity sits.
            </p>
            <ul className="mt-8 divide-y divide-white/5">
              {INTEL.map((row) => (
                <li key={row.k} className="grid gap-1 py-3.5 sm:grid-cols-[180px_1fr] sm:gap-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                    {row.k}
                  </span>
                  <span className="text-sm leading-relaxed text-zinc-300">{row.v}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: 0.12 }}
            data-testid="value-first-card"
            className="flex flex-col border border-[#27272A] bg-[#0A0A0A] p-8 transition-colors duration-300 hover:border-[#D4AF37]/40 lg:p-10"
          >
            <div className="flex items-center gap-3">
              <Gift className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-300">
                Value-First Engine
              </h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-500">
              The first question is never <em className="text-zinc-300">&ldquo;what can we sell?&rdquo;</em>{" "}
              It is <em className="text-zinc-300">&ldquo;what can we give this host, admin or audience
              that is genuinely useful?&rdquo;</em> The Value Agent recommends the offer most likely to
              earn trust — and the Outreach Agent builds the proposal around it.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {VALUE_OFFERS.map((v) => (
                <span
                  key={v}
                  className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-zinc-300 transition-colors duration-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                >
                  {v}
                </span>
              ))}
            </div>
            <div className="mt-auto pt-10">
              <div className="border border-[#047857]/40 bg-[#047857]/10 p-6">
                <p className="flex items-start gap-3 text-sm leading-relaxed text-zinc-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#10B981]" />
                  <span>
                    <span className="font-semibold text-[#10B981]">Next-Best-Action Engine: </span>
                    &ldquo;The creator has engaged twice. This is the right moment to propose a
                    collaboration — lead with the free B2 masterclass, not the product.&rdquo;
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
